import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Chat,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DeckSummary } from '../../types';
import { formatDateTime } from '../../utils/formatting';
import { ReanalysisDialog } from './ReanalysisDialog';

interface DeckListProps {
  decks: DeckSummary[];
  onDelete?: (deckId: string) => void;
  onReanalyzeSuccess?: () => void;
}

export const DeckList: React.FC<DeckListProps> = ({ decks, onDelete, onReanalyzeSuccess }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDeck, setSelectedDeck] = useState<DeckSummary | null>(null);
  const [reanalysisDialogOpen, setReanalysisDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, deck: DeckSummary) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedDeck(deck);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDeck(null);
  };

  const handleDelete = () => {
    if (selectedDeck && onDelete) {
      onDelete(selectedDeck.deckId);
    }
    handleMenuClose();
  };

  const handleReanalyze = () => {
    handleMenuClose();
    setReanalysisDialogOpen(true);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          label: 'Completed',
          color: 'success' as const,
          icon: <CheckCircle fontSize="small" />,
        };
      case 'failed':
        return {
          label: 'Failed',
          color: 'error' as const,
          icon: <ErrorIcon fontSize="small" />,
        };
      case 'processing':
      case 'converting':
        return {
          label: 'Processing',
          color: 'warning' as const,
          icon: <HourglassEmpty fontSize="small" />,
        };
      default:
        return {
          label: 'Pending',
          color: 'default' as const,
          icon: <HourglassEmpty fontSize="small" />,
        };
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Slides</TableCell>
              <TableCell>Issues</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {decks.map((deck) => {
              const statusConfig = getStatusConfig(deck.status);
              return (
                <TableRow
                  key={deck.deckId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/analysis/${deck.deckId}`)}
                >
                  <TableCell>{deck.fileName}</TableCell>
                  <TableCell>
                    <Chip
                      icon={statusConfig.icon}
                      label={statusConfig.label}
                      color={statusConfig.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{deck.totalSlides ?? '-'}</TableCell>
                  <TableCell>
                    {deck.issueCount ? (
                      <Box display="flex" gap={0.5}>
                        <Chip
                          label={deck.issueCount.critical}
                          size="small"
                          color="error"
                          sx={{ minWidth: 32 }}
                        />
                        <Chip
                          label={deck.issueCount.high}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ minWidth: 32 }}
                        />
                        <Chip
                          label={deck.issueCount.low}
                          size="small"
                          color="info"
                          sx={{ minWidth: 32 }}
                        />
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(deck.uploadedAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, deck)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedDeck) {
            navigate(`/analysis/${selectedDeck.deckId}`);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedDeck) {
              navigate(`/chat/${selectedDeck.deckId}`);
            }
            handleMenuClose();
          }}
          disabled={selectedDeck?.status !== 'completed'}
        >
          <ListItemIcon>
            <Chat fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chat</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleReanalyze}
          disabled={
            !selectedDeck || 
            selectedDeck.status === 'processing' || 
            selectedDeck.status === 'converting'
          }
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>Re-analyze Deck</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <ReanalysisDialog
        open={reanalysisDialogOpen}
        onClose={() => setReanalysisDialogOpen(false)}
        deck={selectedDeck}
        onSuccess={onReanalyzeSuccess}
      />
    </>
  );
};

